import React, { useCallback, useState } from 'react';
import isValidDate from 'date-fns/isValid';
import parseISO from 'date-fns/parseISO';
import { createLocalizationProvider } from '@atlaskit/locale';
import { Box, Stack, xcss, Text } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import { standardizeLocale } from '@atlassian/jira-issue-format-date/src/common/utils.tsx';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import type { DefaultDateTimeProps } from '../../common/types.tsx';
import { DateTimePicker as CustomDateTimePicker } from '../../common/ui/date-time-picker/index.tsx';
import messages from './messages.tsx';

type Props = DefaultDateTimeProps;

const ONE_DAY_IN_MILLISECONDS = 86400000;

const isDateValueInvalid = (value: string) => !isValidDate(parseISO(value)) && value !== '';

const BetweenDateTime = ({ value: { value1, value2 }, onChange }: Props) => {
	const locale = useLocale();
	const standardizedLocale = standardizeLocale(locale);
	const [localizationProvider] = useState(createLocalizationProvider(standardizedLocale));
	const { formatMessage } = useIntl();

	const onChangeValue1Date = useCallback(
		(value: string) => {
			onChange({ value1: value, value2 });
		},
		[onChange, value2],
	);

	const onChangeValue2Date = useCallback(
		(value: string) => {
			onChange({ value1, value2: value });
		},
		[onChange, value1],
	);

	return (
		<Box xcss={wrapperStyles} paddingBlockStart="space.075" paddingInlineStart="space.300">
			<Stack space="space.075">
				<CustomDateTimePicker
					locale={standardizedLocale}
					onChange={onChangeValue1Date}
					isInvalid={isDateValueInvalid(value1)}
					dateLabel={formatMessage(messages.startDateLabel)}
					placeholder={localizationProvider.formatDate(
						new Date(new Date().getTime() - ONE_DAY_IN_MILLISECONDS),
					)}
					value={!isDateValueInvalid(value1) ? value1 : ''}
					testId="jql-builder-basic-datetime.ui.between-date-time.value-1"
					timeLabel={formatMessage(messages.startTimeLabel)}
					defaultTime="00:00"
				/>
				<Text>{formatMessage(messages.and)}</Text>
				<CustomDateTimePicker
					locale={standardizedLocale}
					isInvalid={isDateValueInvalid(value2)}
					onChange={onChangeValue2Date}
					dateLabel={formatMessage(messages.endDateLabel)}
					placeholder={localizationProvider.formatDate(
						new Date(new Date().getTime() + ONE_DAY_IN_MILLISECONDS),
					)}
					value={!isDateValueInvalid(value2) ? value2 : ''}
					testId="jql-builder-basic-datetime.ui.between-date-time.value-2"
					timeLabel={formatMessage(messages.endTimeLabel)}
					defaultTime="23:59"
				/>
			</Stack>
		</Box>
	);
};

export default BetweenDateTime;

const wrapperStyles = xcss({ width: '265px' });
